import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from '@phosphor-icons/react';

import { GoalEditor } from '@/components/coach/GoalEditor';

// GoalDialog wraps the GoalEditor in the app's native-<dialog> modal so editing
// goals opens a focused overlay instead of pushing the dashboard content around.
// Mirrors the AdminFormModal pattern: ref-driven showModal()/close(), and the
// dialog's own `close` event routes back to onClose.
export function GoalDialog({
  open,
  onClose,
  slug,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <div className="flex max-h-[85vh] w-[min(40rem,92vw)] flex-col gap-4 overflow-y-auto p-6">
        <header className="flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-foreground">
            {t('coach.dashboard.editGoals')}
          </h2>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={20} weight="bold" aria-hidden />
          </button>
        </header>
        {/* Remount the editor each time the dialog opens so it re-seeds from the
            latest saved goals rather than keeping stale local edits. */}
        {open && <GoalEditor slug={slug} onSaved={onClose} onCancel={onClose} />}
      </div>
    </dialog>
  );
}
